import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import axios from 'axios';
import config from '../../config';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import useAuthenticate from "../authentication/authenticate.js";

const CreateWorkPermitForm = ({ property, propertyId, onClose, onRequestCreated }) => {
  const { user } = useAuthenticate();

  const [units, setUnits] = useState([]);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    unit: '',
    requestedBy: '',
    managedBy: [],
    assignees: [],
    type: 'WorkPermit',
    status: 'Pending',
    description: '',
    details: {
      title: '',
      serviceProvider: '',
      workCategory: '',
      startDate: '',
      endDate: '',
    },
  });

  // Fetching current user account
  useEffect(() => {
    if (user?.id) {
      const role = Object.keys(user.active || {})[0]; // manager, owner, etc.
      const activeAccountId = user.active?.[role] ?? null;
      setFormData(prev => ({ ...prev, requestedBy: activeAccountId }));
    }
  }, [user]);

  // Fetching units for the selected property
  useEffect(() => {
    const fetchData = async () => {
      try {
        const unitRes = await axios.get(`${config.API_URL}/v2/unit`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
        setUnits(unitRes.data);
      } catch (err) {
        setError("Failed to fetch units or accounts.");
      }
    };
    fetchData();
  }, [propertyId]);

  const handleSubmit = async () => {
    if (!formData.unit || !formData.description ||
      !formData.details.serviceProvider || !formData.details.workCategory ||
      !formData.details.startDate || !formData.details.endDate) {
      setError('Please fill out all required fields.');
      return;
    }

    try {
      const url = `${config.API_URL}/v2/request`;

      const response = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}`, 'Content-Type': 'application/json' }
      });

      console.log('Work permit created:', response.data);

      if (response.status === 200 || response.status === 201) {
        onRequestCreated?.(response.data);
        toast.success("Work permit created successfully.");
        onClose();
      } else {
        setError('Failed to submit the work permit.');
      }
    } catch (err) {
      setError('Error creating the work permit. Please try again.');
      toast.error('Failed to create the work permit.');
      console.error(err);
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <button className="close-popup" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
        <h2 className="text-2xl text-black">File Work Permit</h2>

        <div className="popup-columns">
          <Autocomplete
            options={units.filter(u => u.property?._id === property.property._id)}
            getOptionLabel={(u) => u.unitNumber}
            value={units.find(u => u._id === formData.unit) || null}
            onChange={(e, newVal) => {
              setFormData(prev => ({ ...prev, unit: newVal?._id }));
            }}
            renderInput={(params) => <TextField {...params} label="Unit" required />}
            sx={{ flex: '1 1 33%', marginBottom: '1rem' }}
          />

          <TextField
            label="Filed By"
            fullWidth
            value={user?.user?.email || ''}
            disabled
            sx={{ flex: '1 1 67%', marginBottom: '1rem' }}
          />
        </div>
        <TextField
          label="Request Title"
          name="title"
          fullWidth
          value={formData.details.title}
          onChange={(event) => {
            const newValue = event.target.value;
            setFormData((prevData) => ({
              ...prevData,
              details: {
                ...prevData.details,
                title: newValue,
              },
            }));
          }}
          sx={{ marginBottom: '1rem' }}
          required
        />

        <TextField
          label="Service Provider"
          fullWidth
          required
          value={formData.details.serviceProvider}
          onChange={(e) =>
            setFormData(prev => ({
              ...prev,
              details: { ...prev.details, serviceProvider: e.target.value },
            }))
          }
          sx={{ marginBottom: '1rem' }}
        />

        <Autocomplete
          options={['Wifi', 'Plumbing', 'Electrical', 'Cleaning', 'Renovation', 'Painting']}
          value={formData.details.workCategory}
          onChange={(event, newValue) => {
            setFormData(prev => ({
              ...prev,
              details: { ...prev.details, workCategory: newValue || '' },
            }));
          }}
          renderInput={(params) => (
            <TextField {...params} label="Work Category" required />
          )}
          sx={{ marginBottom: '1rem' }}
        />

        <TextField
          label="Work Description"
          multiline
          fullWidth
          rows={3}
          required
          value={formData.description}
          onChange={(e) =>
            setFormData(prev => ({
              ...prev,
              description: e.target.value,
            }))
          }
          sx={{ marginBottom: '1rem' }}
        />

        <div className="popup-columns" style={{ marginBottom: '1rem' }}>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={formData.details.startDate ? dayjs(formData.details.startDate) : null}
              onChange={(newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  details: {
                    ...prev.details,
                    startDate: newValue ? newValue.format('YYYY-MM-DD') : '',
                  },
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                />
              )}
            />

            <DatePicker
              label="End Date"
              value={formData.details.endDate ? dayjs(formData.details.endDate) : null}
              onChange={(newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  details: {
                    ...prev.details,
                    endDate: newValue ? newValue.format('YYYY-MM-DD') : '',
                  },
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                />
              )}
            />
          </LocalizationProvider>
        </div>

        <button className="button" onClick={handleSubmit}>File Work Permit</button>
        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
};

export default CreateWorkPermitForm;